<template>
  <div class="experience">
    <div class="container-fluid">
      <h1>Experience</h1>
      <p>
        My interests and skills are varied, which is reflected in this overview of work experience.
        I am currently doing a graduation internship at the Province of South Holland, where I do research into sustainable alternative energy carriers for the inland shipping sector. My experience volunteering and at TU Delft's green office is also listed.
        I have also worked as a web developer, and continue doing web development on freelance basis. A portfolio overview can be found on the <a href="#/portfolio">portfolio tab</a>!
      </p>
    </div>
    <div class="container-fluid">
      <h2>Work experience</h2>
    </div>
    <PanelsList :panels="workPanels" class="mb-3" />
    <div class="container-fluid">
      <h2>Volunteering</h2>
    </div>
    <PanelsList :panels="volunteerPanels" class="mb-3" />
  </div>
</template>

<script>
import PanelsList from "@/components/PanelsList";

export default {
  name: "Experience",
  components: {
    PanelsList
  },
  data() {
    return {
      workPanels: [
        {
          title: "Blue Book Trainee",
          description: {
            text: "Trainee at the DG CLIMA's unit for low-carbon technologies.",
            image: "logo/ec.png",
            icons: [
              { icon: "building", title: "Employer", text: "European Commission" },
              {
                icon: "calendar-week",
                title: "Date",
                text: "March 2025 - July 2024"
              }
            ]
          },
          content: {
            text:
              "The Blue Book traineeship is a five-month programme at the European Commission. I did my traineeship at the Directorate-General for Climate Action (DG CLIMA), in the unit for low-carbon technology research and deployment. During my traineeship, I assisted in data collection for the monitoring of EU-funded carbon management projects, and led the development of an AI-based tool for document evaluation and policy analysis.",
            image: "logo/ec.png",
            icons: [
              { icon: "building", title: "Employer", text: "European Commission" },
              {
                icon: "calendar-week",
                title: "Date",
                text: "March 2025 - July 2024"
              }
            ]
          }
        },
        {
          title: "Intern sustainable shipping",
          description: {
            text: "LCA into sustainable energy carriers for inland shipping in South Holland.",
            image: "logo/pzh.png",
            icons: [
              { icon: "building", title: "Employer", text: "Provincie Zuid-Holland" },
              {
                icon: "calendar-week",
                title: "Date",
                text: "September 2023 - February 2024"
              }
            ]
          },
          content: {
            text:
              "As Industrial Ecology graduation project and internship at the Province of South Holland, I am performed research into sustainable inland shipping, considering technical aspects and the surrounding policy context. In this research project, based on a life-cycle assessment (LCA), I compare various alternative energy carriers for cargo barges, which currently run primarily on polluting diesel engines, but must make a sustainable transition in the coming decades. I also developed a web-based tool to visualise LCA results in an interactive way.",
            image: "logo/pzh.png",
            icons: [
              { icon: "building", title: "Employer", text: "Provincie Zuid-Holland" },
              {
                icon: "calendar-week",
                title: "Date",
                text: "September 2023 - February 2024"
              }
            ],
            links: [
              {
                icon: "info-circle",
                title: "Read my thesis!",
                url: "https://static.quan.cat/inlandshipping"
              }
            ]
          }
        },
        {
          title: "Secretary",
          description: {
            text:
              "Secretary of the 2022-2023 board of GreenTU, the TU Delft Green Office.",
            image: "logo/greentu2022.png",
            icons: [
              {
                icon: "building",
                title: "Employer",
                text: "GreenTU Delft"
              },
              {
                icon: "calendar-week",
                title: "Date",
                text: "September 2022 - August 2023"
              }
            ]
          },
          content: {
            text:
              'GreenTU is the green office of TU Delft, the university\'s central organisation for sustainability in Research, Education, Operations, and Community, run by an annual student board. As secretary of the 2022-2023 board, I kept track of our team\'s planning and internal communication, and worked on profesionallising our branding and image. I was also responsible for the research portfolio, the education portfolio (shared), and for developing the online GreenDatabase.',
            image: "logo/greentu2022.png",
            icons: [
              {
                icon: "building",
                title: "Employer",
                text: "GreenTU Delft"
              },
              {
                icon: "calendar-week",
                title: "Date",
                text: "September 2022 - August 2023"
              }
            ],
            links: [
              {
                icon: "info-circle",
                title: "More about GreenTU",
                url: "https://tudelft.nl/greentu"
              }
            ]
          }
        },
        {
          title: "Front-end web developer",
          description: {
            text:
              "Development work on web sites and online applications (part-time).",
            image: "logo/fenetre.png",
            icons: [
              {
                icon: "building",
                title: "Employer",
                text: "Fenêtre Online Solutions"
              },
              {
                icon: "calendar-week",
                title: "Date",
                text: "August 2017 - August 2023"
              }
            ]
          },
          content: {
            icons: [
              {
                icon: "building",
                title: "Employer",
                text: "Fenêtre Online Solutions"
              },
              {
                icon: "calendar-week",
                title: "Date",
                text: "August 2017 - August 2023"
              }
            ],
            text:
              "At Fenêtre I contributed to the development of web sites and online applications, mainly targeted at business clients. My main focus was on front-end development, using HTML and CSS (and preprocessors, e.g. SCSS), JavaScript and Vue.js, as well as C# and Umbraco.",
            image: "logo/fenetre.png"
          }
        },
        {
          title: "Intern physics teacher",
          description: {
            text: "Teacher training and practice as part of Education minor.",
            image: "logo/ly.png",
            icons: [
              { icon: "building", title: "Employer", text: "Lyceum Ypenburg" },
              {
                icon: "calendar-week",
                title: "Date",
                text: "September 2019 - January 2020"
              }
            ]
          },
          content: {
            text:
              "For TU Delft's minor in Education, I practised teaching at Lyceum Ypenburg in The Hague, where I taught high school Physics to HAVO and VWO students.",
            image: "logo/ly.png",
            icons: [
              { icon: "building", title: "Employer", text: "Lyceum Ypenburg" },
              {
                icon: "calendar-week",
                title: "Date",
                text: "September 2019 - January 2020"
              }
            ]
          }
        }
      ],
      volunteerPanels: [
        {
          title: "Board member",
          description: {
            text:
              "Board member for a local chapter of the Dutch Cyclists' Union.",
            image: "logo/fb.png",
            icons: [
              {
                icon: "building",
                title: "Employer",
                text: "Fietsersbond Rijswijk"
              },
              {
                icon: "calendar-week",
                title: "Date",
                text: "September 2022 - current"
              }
            ]
          },
          content: {
            text:
              'As a local chapter of the Cyclist\'s Union, we promote cycling as mode of sustainable transportation. This is done both from the angle of public engagement and awareness, and lobbying the municipal government to adopt effective and modern mobility policies.',
            image: "logo/fb.png",
            icons: [
              {
                icon: "building",
                title: "Employer",
                text: "Fietsersbond Rijswijk"
              },
              {
                icon: "calendar-week",
                title: "Date",
                text: "September 2022 - current"
              }
            ]
          }
        },
        {
          title: "Research Committee",
          description: {
            text:
              "Volunteer member of the Research Committee at GreenTU.",
            image: "logo/greentu.png",
            icons: [
              {
                icon: "building",
                title: "Employer",
                text: "GreenTU Delft"
              },
              {
                icon: "calendar-week",
                title: "Date",
                text: "September 2021 - August 2022"
              }
            ]
          },
          content: {
            text:
              'GreenTU is the Green Office of TU Delft, a student-run organisation for sustainability in Research, Education, Operations, and Community. As a member of the Research Committee, I help write GreenTU\'s annual "Vision on Sustainability" report. I followed this up by becoming a board member of GreenTU in the next academic year.',
            image: "logo/greentu.png",
            icons: [
              {
                icon: "building",
                title: "Employer",
                text: "GreenTU Delft"
              },
              {
                icon: "calendar-week",
                title: "Date",
                text: "September 2021 - August 2022"
              }
            ],
            links: [
              {
                icon: "info-circle",
                title: "Read the Vision on Sustainability report",
                url: "https://www.tudelft.nl/en/greentu/projects/vision-on-sustainability"
              }
            ]
          }
        },
        {
          title: "Fingerprint Art",
          description: {
            text:
              "Co-founder, team member and web developer for a social art project.",
            image: "logo/nobis.png",
            icons: [
              {
                icon: "building",
                title: "Employer",
                text: "NOBIS / Canidream"
              },
              {
                icon: "calendar-week",
                title: "Date",
                text: "2019 - 2021"
              }
            ]
          },
          content: {
            text:
              "Following a project for an interdisciplinary university course, my fellow team members and I set on to make our proposal for a social street art project a reality. We reached out to a local community and organised the creation of a multiple-storey mural in Buitenhof (Delft). Our group, NOBIS, is now part of one of the project's main collaborators, Stichting Canidream.",
            image: "logo/nobis.png",
            icons: [
              {
                icon: "building",
                title: "Employer",
                text: "NOBIS / Canidream"
              },
              {
                icon: "calendar-week",
                title: "Date",
                text: "2019 - 2021"
              }
            ],
            links: [
              {
                icon: "info-circle",
                title: "More about NOBIS",
                url: "https://nobisdelft.nl/"
              }
            ]
          }
        },
      ]
    };
  }
};
</script>
